import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Checkbox,
} from "@mui/material";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import { TableVirtuoso } from "react-virtuoso";
import useHttpRequests from "../../../hooks/useHttpRequests";

const thBackgoundColor = "#3a8ddd";
const thFontColor = "white";
const thFontWeight = "bold";
const paperStyles = { width: "100%", height: "calc(100vh - 50px)" };

const columns = [
  {
    label: "",
    keyField: "columnKey",
    dataField: "isChecked",
    width: 30,
    fieldType: "checkbox",
  },
  {
    label: "Column Name",
    keyField: "columnKey",
    dataField: "name",
    width: 300,
    fieldType: "string",
  },
  {
    label: "Column Number",
    keyField: "columnKey",
    dataField: "columnNumber",
    fieldType: "number",
    numeric: true,
    width: 90,
  },
  {
    label: "PHI",
    keyField: "columnKey",
    dataField: "phi",
    fieldType: "boolean",
    width: 50,
  },
];

const VirtuosoTableComponents = {
  Scroller: React.forwardRef((props, ref) => (
    <TableContainer
      component={Paper}
      {...props}
      ref={ref}
      sx={{ height: "calc(100% - 45px) !important" }}
    />
  )),
  Table: (props) => (
    <Table
      {...props}
      sx={{ borderCollapse: "separate", tableLayout: "fixed" }}
    />
  ),
  TableHead,
  TableRow: ({ item: _item, ...props }) => <TableRow {...props} />,
  TableBody: React.forwardRef((props, ref) => (
    <TableBody {...props} ref={ref} />
  )),
};

export const FileTypeColumnAdd = (props) => {
  const { setSelectedRows } = props;
  const [localData, setLocalData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const { sendRequest: fetchColumns } = useHttpRequests();

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const filteredData = localData.filter((row) =>
    columns.some(
      (col) =>
        col.fieldType === "string" &&
        row[col.dataField]?.toLowerCase().includes(searchQuery?.toLowerCase())
    )
  );

  const selectedRows = filteredData.filter((row) => row.isChecked);
  const remainingRows = filteredData.filter((row) => !row.isChecked);
  const orderedData = [...selectedRows, ...remainingRows];

  const fixedHeaderContent = () => {
    return (
      <TableRow>
        {columns.map((column) => (
          <TableCell
            key={column.dataField}
            variant="head"
            align={column["fieldType"] === "boolean" ? "center" : "left"}
            style={{
              backgroundColor: thBackgoundColor,
              color: thFontColor,
              fontWeight: thFontWeight,
              padding: "8px 0px 8px 8px",
              width: column.width,
            }}
            sx={{
              backgroundColor: "background.paper",
            }}
          >
            {column.label}
          </TableCell>
        ))}
      </TableRow>
    );
  };

  const rowContent = (_index, row) => {
    return (
      <React.Fragment>
        {columns.map((col, colIdx) => (
          <TableCell
            sx={{ padding: "0px 0px 0px 8px" }}
            key={"cols_" + colIdx + "_" + row[col["keyField"]]}
            align={col["fieldType"] === "boolean" ? "center" : "left"}
          >
            {col["fieldType"] === "checkbox" && col["label"] === "" && (
              <Checkbox
                sx={{ p: 0 }}
                checked={row.isChecked}
                onChange={() => handleCheckboxChange(row.columnKey)}
              />
            )}
            {row[col["keyField"]] !== row[col["dataField"]] &&
              col["fieldType"] !== "number" &&
              col["dataType"] !== "date" && <>{row[col["dataField"]]}</>}
            {col["fieldType"] === "number" && (
              <TextField
                type={col["fieldType"]}
                size="small"
                id={
                  col["fieldType"] + "_" + colIdx + "_" + row[col["keyField"]]
                }
                name={col["dataField"]}
                onChange={(event) =>
                  handleInputChange(event, row[col["keyField"]])
                }
                value={row[col["dataField"]]}
                maxLength={2}
              />
            )}
            {typeof row[col["dataField"]] === "boolean" &&
              col["fieldType"] !== "checkbox" && (
                <div
                  style={{
                    cursor: "pointer",
                    color: row[col["dataField"]] ? "green" : "grey",
                  }}
                  onClick={(e) => {
                    changeStatus(row.columnKey, col["dataField"]);
                  }}
                >
                  {row[col["dataField"]] ? (
                    <ToggleOnIcon
                      sx={{
                        fontSize: "2rem",
                      }}
                    />
                  ) : (
                    <ToggleOffIcon
                      sx={{
                        fontSize: "2rem",
                      }}
                    />
                  )}
                </div>
              )}
          </TableCell>
        ))}
      </React.Fragment>
    );
  };

  useEffect(() => {
    const getColumnsResponse = (obj) => {
      if (!obj || !obj.isSuccess || obj.errorResponse) {
        console.log("Columns fetch response returns error");
        return;
      }
      setLocalData(
        obj["columns"]?.map((t) => {
          return {
            columnKey: t["columnKey"],
            name: t["name"],
            columnNumber: "",
            phi: false,
            active: true,
            isChecked: false,
          };
        })
      );
    };

    fetchColumns(
      { url: "Columns", method: "GET", header: true },
      getColumnsResponse
    );
  }, []);

  const handleCheckboxChange = (columnKey) => {
    const updatedData = localData.map((row) => {
      if (row.columnKey === columnKey) {
        return { ...row, isChecked: !row.isChecked };
      }
      return row;
    });

    setLocalData(updatedData);
    setSelectedRows(updatedData.filter((row) => row.isChecked));
  };

  const changeStatus = (key, dataField) => {
    const updatedData = localData?.map((row) => {
      if (row.columnKey === key) {
        return { ...row, [dataField]: !row[dataField] };
      }
      return row;
    });

    setLocalData(updatedData);
  };

  const handleInputChange = (event, id) => {
    const { name, value } = event.target;
    setLocalData((prevData) =>
      prevData.map((row) =>
        row[columns[0]["keyField"]] === id ? { ...row, [name]: value } : row
      )
    );
    setSelectedRows((prevData) =>
      prevData.map((row) =>
        row[columns[0]["keyField"]] === id ? { ...row, [name]: value } : row
      )
    );
  };

  return (
    <Paper sx={paperStyles}>
      <TextField
        size="small"
        sx={{ float: "right", marginBottom: "8px" }}
        placeholder="Search"
        value={searchQuery}
        onChange={handleSearch}
      />
      <TableVirtuoso
        data={orderedData}
        components={VirtuosoTableComponents}
        fixedHeaderContent={fixedHeaderContent}
        itemContent={rowContent}
      />
    </Paper>
  );
};
